import { Button } from "@/components/ui/button";
import * as React from "react";
import { ChevronDownIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCarServices } from "@/services/getCarServices";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { Role } from "@/core/auth/models/role.model";
import RoleGuard from "@/core/auth/components/role-guard";
// import { columns } from "./columns";
import { updateCarService } from "@/services/updateCarServices";

import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { deleteCarService } from "@/services/deleteCarService";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchCategories } from "@/services/getCategories";
import { useState } from "react";
import { Edit, Pen, Trash } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { set } from "zod";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

function CarServicesList() {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [currentCarService, setCurrentCarService] = React.useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [serviceToDelete, setServiceToDelete] = React.useState<any>(null);
  const [subcategories, setSubcategories] = useState([]);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const CategoryData = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await fetchCategories();
      return data.map((category: any) => {
        return { ...category, company: category.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const {
    data: carServices,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["car-services"],
    queryFn: async () => getCarServices(),
    select: (data) => {
      return data.map((service: any) => ({
        ...service,
        companyName: service.company.name,
      }));
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const mutation = useMutation({
    mutationFn: (carService: any) => {
      const updatedService = {
        _id: carService._id,
        name: carService.name,
        description: carService.description,
        price: carService.price,
        duration: carService.duration,
        category: carService.category?._id || carService.category,
        subCategory: carService.subCategory?._id || carService.subCategory,
        company: carService.company?._id || carService.company,
      };
      return updateCarService(updatedService._id, updatedService);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["car-services"] });
      setIsEditModalOpen(false);
      toast.success("Car service updated successfully");
    },
    onError: (error) => {
      toast.error(error.message);
      console.error(error);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteCarService(serviceToDelete?._id, user?.company?._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["car-services"] });
      setIsDeleteModalOpen(false);
      toast.success("Car service deleted successfully");
    },
    onError: (error) => {
      toast.error(error.message);
      console.error(error);
    },
  });
  const handleCategoryChange = (categoryId: string) => {
    const selectedCategory = CategoryData.data?.find(
      (category: any) => category._id === categoryId
    );
    setSubcategories(selectedCategory?.subcategories || []);
    setCurrentCarService((prevService: any) => ({
      ...prevService,
      category: categoryId,
      subCategory: selectedCategory?.subcategories?.some((sub: any) => sub._id === prevService.subCategory)
        ? prevService.subCategory
        : "",
    }));
  };
  // No react-table, render table manually
  return (
    <div className="md:p-4">
      {isPending ? (
        [1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
      ) : isError ? (
        <div>Error: {error.message}</div>
      ) : (
        <>
          <ScrollArea className="rounded-md border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  {/* company  */}
                  <TableHead>Company</TableHead>
                  <TableHead>Price (KES)</TableHead>
                  <TableHead>Duration (mins)</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Subcategory</TableHead>
                  <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
                    <TableHead>Actions</TableHead>
                  </RoleGuard>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(carServices) && carServices.length > 0 ? (
                  carServices.map((service: any) => (
                    <TableRow key={service._id}>
                      <TableCell>{service.name}</TableCell>
                      <TableCell>{service.description || "-"}</TableCell>
                      <TableCell>{service.companyName || "-"}</TableCell>
                      <TableCell>{service.price || "-"}</TableCell>
                      <TableCell>{service.duration || "-"}</TableCell>
                      <TableCell>{service.category?.name || "-"}</TableCell>
                      <TableCell>{service.subCategory?.name || "-"}</TableCell>
                      <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="border-none"
                                    onClick={() => {
                                      setCurrentCarService(service);
                                      const selectedCategory = CategoryData.data?.find(
                                        (category: any) => category._id === (service.category?._id || service.category)
                                      );
                                      setSubcategories(selectedCategory?.subcategories || []);
                                      setIsEditModalOpen(true);
                                    }}
                                  >
                                    <Pen className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="border-none"
                                    onClick={() => {
                                      setServiceToDelete(service);
                                      setIsDeleteModalOpen(true);
                                    }}
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </RoleGuard>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </>
      )}

      {isEditModalOpen && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Edit Car Service</DialogTitle>
            <div className="grid grid-cols-2 gap-4 w-full">             
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={currentCarService?.name}
                  onChange={(event) =>
                    setCurrentCarService({
                      ...currentCarService,
                      name: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={currentCarService?.description}
                  onChange={(event) =>
                    setCurrentCarService({
                      ...currentCarService,
                      description: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  value={currentCarService?.price}
                  onChange={(event) =>
                    setCurrentCarService({
                      ...currentCarService,
                      price: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  value={currentCarService?.duration}
                  onChange={(event) =>
                    setCurrentCarService({
                      ...currentCarService,
                      duration: event.target.value,
                    })
                  }
                />
              </div>      
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={currentCarService?.category?._id || currentCarService?.category || ""}
                  onValueChange={(value) => handleCategoryChange(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category">
                      {CategoryData.data?.find((cat: any) => 
                        cat._id === (currentCarService?.category?._id || currentCarService?.category)
                      )?.name || "Select category"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CategoryData.data?.map((category: any) => (
                      <SelectItem key={category._id} value={category._id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subcategory">Subcategory</Label>
                <Select
                  value={currentCarService?.subCategory?._id || currentCarService?.subCategory || ""}
                  onValueChange={(value) =>
                    setCurrentCarService({
                      ...currentCarService,
                      subCategory: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory">
                      {subcategories.find((sub: any) => 
                        sub._id === (currentCarService?.subCategory?._id || currentCarService?.subCategory)
                      )?.name || "Select subcategory"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map((subCategory: any) => (
                      <SelectItem key={subCategory._id} value={subCategory._id}>
                        {subCategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex justify-end w-full gap-2">
                {/* cancel button  */}
                <Button
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                  // className="w-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => mutation.mutate(currentCarService)}
                  // className="w-full"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {isDeleteModalOpen && (
        // <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Delete Car Service</DialogTitle>
            <div className="space-y-4">
              <p>Are you sure you want to delete this car service?</p>
              <div className="flex justify-end w-full gap-2">
                {/* cancel button  */}
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteModalOpen(false)}
                  // className="w-full"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteMutation.mutate(serviceToDelete?._id);
                  }}
                  // className="w-full"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default CarServicesList;
